import type { MouseEvent, ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { FilterItemClearButton } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FIND_APPS_BUTTON_SIZE } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { IFilterItemCardActionsProps } from "../../components/FilterPanelItem/types";
import advancedFilteringI18nKeys from "../../utils/i18n";

import { StyledFilterCardActions, StyledPinButton, StyledPinButtonWrapper } from "./styles";

interface IProps<T extends IPlainObject> extends Omit<IFilterItemCardActionsProps<T>, "key"> {
    isShowPinButton: boolean;
    isPinDisabled?: boolean;
    isPinned: boolean;
    onPinClick: VoidFunction;
}

const FilterCardActions = <T extends IPlainObject = IPlainObject>({
    isPinDisabled = false,
    isShowPinButton,
    isPinned,
    isShowClearButton = false,
    onPinClick,
    onClear,
}: IProps<T>): ReactElement => {
    const { translate } = useTranslate();

    const clearButton = isShowClearButton ? <FilterItemClearButton onClick={onClear} /> : null;

    // Only an actually-pinned filter can be locked: a card kept visible purely by its value (e.g. a team
    // filter) is still unpinned, so its pin stays available rather than showing a "cannot unpin" lock.
    const isPinLockedByValue = isPinned && isShowClearButton;
    const isPinClickDisabled = isPinDisabled || isPinLockedByValue;

    const handlePinClick = (e: MouseEvent) => {
        e.stopPropagation();

        if (isPinClickDisabled) {
            return;
        }

        onPinClick();
    };

    const pinButtonNode = (
        <StyledPinButtonWrapper
            isPinned={isPinned}
            isDisabled={isPinClickDisabled}
            data-automation-filter-pinned={isPinned}
            onClick={(e) => e.stopPropagation()}
        >
            <StyledPinButton
                type="flat"
                iconSize="xs"
                iconColor={isPinned && !isPinDisabled ? colorsPalettes.blue[400] : colorsPalettes.carbon[200]}
                isDisabled={isPinClickDisabled}
                height={FIND_APPS_BUTTON_SIZE}
                width={FIND_APPS_BUTTON_SIZE}
                iconName="pin"
                dataAutomation="filters-item.pin-button"
                onClick={handlePinClick}
            />
        </StyledPinButtonWrapper>
    );

    return (
        <StyledFilterCardActions>
            {clearButton}

            {isShowPinButton ? (
                isPinLockedByValue ? (
                    <Tooltip content={translate(advancedFilteringI18nKeys.cannotUnpinActiveFilterTooltip)}>
                        <div>{pinButtonNode}</div>
                    </Tooltip>
                ) : (
                    pinButtonNode
                )
            ) : null}
        </StyledFilterCardActions>
    );
};

export default FilterCardActions;
